import { useEffect, useRef, useState } from 'react';
import {
  TIME_CLASSES,
  TIME_CONTROLS,
  allowsRatedPlay,
  defaultTimeControlForClass,
  timeControlsForClass
} from '../../chess/game.js';
import {
  BACKLOG_VARIANTS,
  PLAYABLE_VARIANTS,
  VARIANTS
} from '../../chess/variants.js';
import { formatDisplayName } from '../../p2p/displayName.js';
import {
  CHALLENGE_DELAY_OPTIONS,
  CHALLENGE_JOIN_WAIT_OPTIONS,
  CHALLENGE_START_MODES,
  formatChallengeSchedule,
  MATCH_FORFEIT_GRACE_MS,
  toDateTimeLocalValue
} from '../../p2p/challenges.js';
import { mergeOnlinePlayers } from '../../p2p/onlinePlayers.js';
import { listGames, saveGame } from '../../chess/gameStore.js';
import {
  continueMatchUrl,
  formatSavedMatchLabel,
  isContinuableMatch,
  parseMatchExport
} from '../../chess/matchExport.js';
import { publicUrl } from '../../app/publicUrl.js';
import { useI18n } from '../../i18n/useI18n.js';
import LobbyChat from './LobbyChat.jsx';

function challengeLabel({ playMode, variant, timeControl, rated, t }) {
  const variantLabel = playMode === 'standard'
    ? ''
    : `${VARIANTS[variant].label} `;
  const ratedLabel = rated ? 'rated ' : '';
  return t('home.createChallenge', {
    label: `${ratedLabel}${variantLabel}${timeControl} `
  });
}

export default function PlayHome({
  nickname,
  playerId,
  joined,
  remotePeers = [],
  helloMessages = [],
  onChallengePlayer,
  inviteUrl = '',
  onCopyInvite,
  timeControl,
  setTimeControl,
  timeClass,
  setTimeClass,
  playMode,
  setPlayMode,
  variant,
  setVariant,
  rated,
  setRated,
  createChallenge,
  openNetworkSettings,
  incomingChallenges = [],
  onAcceptChallenge,
  onDeclineChallenge,
  pendingOutgoingChallenge = null,
  onOpenPendingOutgoingBoard,
  onCancelPendingOutgoingChallenge,
  openToChallenges,
  onToggleOpenToChallenges,
  joinWaitMs,
  setJoinWaitMs,
  scheduledStartDraft,
  setScheduledStartDraft,
  startMode,
  setStartMode,
  startDelayMs,
  setStartDelayMs,
  lobbyChat = null,
  onContinueMatch
}) {
  const { t, duration } = useI18n();
  const selected = TIME_CONTROLS[timeControl];
  const visibleControls = timeControlsForClass(timeClass);
  const selectedVariant = VARIANTS[variant];
  const scheduleMin = toDateTimeLocalValue(Date.now() + 60_000);
  const onlinePlayers = mergeOnlinePlayers(remotePeers, helloMessages, {
    selfPlayerId: playerId
  });
  const [savedMatches, setSavedMatches] = useState([]);
  const [continueNotice, setContinueNotice] = useState('');
  const importInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    listGames()
      .then((games) => {
        if (cancelled) {
          return;
        }
        setSavedMatches(
          games.filter((game) =>
            isContinuableMatch(game)
            && (
              game.players?.white?.playerId === playerId
              || game.players?.black?.playerId === playerId
            )
          ).slice(0, 8)
        );
      })
      .catch(() => {
        if (!cancelled) {
          setSavedMatches([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [playerId]);

  function selectTimeClass(nextClass) {
    setTimeClass(nextClass);
    const nextControl = defaultTimeControlForClass(nextClass);
    setTimeControl(nextControl);
    if (!allowsRatedPlay(nextControl)) {
      setRated(false);
    }
  }

  function selectTimeControl(nextControl) {
    setTimeControl(nextControl);
    if (!allowsRatedPlay(nextControl)) {
      setRated(false);
    }
  }

  function selectPlayMode(nextMode) {
    setPlayMode(nextMode);
    if (nextMode === 'standard') {
      setVariant('standard');
      return;
    }
    if (variant === 'standard') {
      setVariant('chess960');
    }
  }

  async function continueSavedMatch(game) {
    const url = continueMatchUrl(game, playerId, publicUrl);
    if (onContinueMatch) {
      onContinueMatch(url);
      return;
    }
    window.location.assign(url);
  }

  async function importMatchFile(file) {
    if (!file) {
      return;
    }
    try {
      const text = await file.text();
      const { game } = parseMatchExport(text);
      await saveGame(game);
      setContinueNotice(`Imported ${game.id.slice(0, 8)} · rev ${game.revision}`);
      setSavedMatches((current) => {
        const without = current.filter((entry) => entry.id !== game.id);
        return [game, ...without].filter(isContinuableMatch).slice(0, 8);
      });
      await continueSavedMatch(game);
    } catch (error) {
      setContinueNotice(error.message || 'Could not import that match file');
    }
  }

  return (
    <>
      <LobbyChat
        joined={joined}
        members={onlinePlayers}
        rooms={lobbyChat?.rooms || []}
        activeRoomId={lobbyChat?.activeRoomId}
        messages={lobbyChat?.messages || []}
        sending={lobbyChat?.sending}
        error={lobbyChat?.error || ''}
        onSendMessage={lobbyChat?.sendMessage}
        onCreateRoom={lobbyChat?.createRoom}
        onSelectRoom={lobbyChat?.selectRoom}
        onChallengePlayer={onChallengePlayer}
        incomingChallenges={incomingChallenges}
        onAcceptChallenge={onAcceptChallenge}
        onDeclineChallenge={onDeclineChallenge}
        pendingOutgoingChallenge={pendingOutgoingChallenge}
        onOpenPendingOutgoingBoard={onOpenPendingOutgoingBoard}
        onCancelPendingOutgoingChallenge={onCancelPendingOutgoingChallenge}
      />

      {!onlinePlayers.length && (
        <div className="lobby-chat-aux">
          <button
            type="button"
            className="text-button"
            disabled={!inviteUrl || !onCopyInvite}
            onClick={() => onCopyInvite?.()}
          >
            {t('home.copyInvite')}
          </button>
          <button type="button" className="text-button" onClick={openNetworkSettings}>
            {t('nav.account')}
          </button>
        </div>
      )}

      <section className="panel continue-match-panel">
        <p className="eyebrow">{t('home.continueEyebrow')}</p>
        <h2>{t('home.continueTitle')}</h2>
        <p className="muted">{t('home.continueLede')}</p>
        <div className="continue-match-actions">
          <input
            ref={importInputRef}
            type="file"
            accept=".json,.dechess.json,application/json"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              void importMatchFile(file);
            }}
          />
          <button
            type="button"
            className="secondary"
            onClick={() => importInputRef.current?.click()}
          >
            {t('home.importMatch')}
          </button>
        </div>
        {continueNotice && (
          <p className="muted" role="status">{continueNotice}</p>
        )}
        {savedMatches.length > 0 && (
          <ul className="continue-match-list">
            {savedMatches.map((game) => {
              const opponent = game.players.white?.playerId === playerId
                ? game.players.black
                : game.players.white;
              const opponentLabel = opponent?.nickname
                || (opponent?.playerId
                  ? formatDisplayName(null, opponent.playerId)
                  : null);
              return (
                <li key={game.id}>
                  <button
                    type="button"
                    className="text-button"
                    title={game.id}
                    onClick={() => continueSavedMatch(game)}
                  >
                    {formatSavedMatchLabel(game, {
                      opponentLabel,
                      openSeatLabel: t('home.continueOpenSeat'),
                      locale: undefined
                    })}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="welcome-hero">
        <div className="welcome-copy">
          <span className="welcome-pill">{t('home.pill')}</span>
          <p className="eyebrow">{t('home.welcome', { name: formatDisplayName(nickname, playerId) })}</p>
          <h1>{t('home.title')}</h1>
          <p className="welcome-lede">{t('home.lede')}</p>
          <div className="welcome-actions">
            <button type="button" className="large-cta" onClick={createChallenge}>
              {challengeLabel({ playMode, variant, timeControl, rated, t })}
            </button>
            <button type="button" className="text-button" onClick={openNetworkSettings}>
              {t('home.configure')}
            </button>
          </div>
          <div className={`friendly-status ${joined ? 'online' : ''}`}>
            <span className="status-dot" />
            {joined ? t('home.ready') : t('home.connecting')}
          </div>
        </div>

        <div className="hero-board" aria-hidden="true">
          <img className="hero-logo" src={publicUrl('logo.png')} alt="" width="260" height="260" />
          <div className="hero-orbit orbit-one" />
          <div className="hero-orbit orbit-two" />
          <span className="floating-piece pawn-one">♙</span>
          <span className="floating-piece pawn-two">♟</span>
        </div>
      </section>


      <section className="play-section">
        <div className="play-heading">
          <div>
            <p className="eyebrow">{t('home.choose')}</p>
            <h2>{t('home.what')}</h2>
          </div>
          <p className="muted">
            {playMode === 'standard'
              ? `${selected.label} · ${t(`classCopy.${selected.class}`)}`
              : `${selectedVariant.label} · ${selected.label}${rated ? t('home.ratedSuffix') : ''}`}
          </p>
        </div>

        <div className="play-mode-toggle" role="tablist" aria-label={t('home.mode')}>
          <button
            type="button"
            role="tab"
            aria-selected={playMode === 'standard'}
            className={playMode === 'standard' ? 'active' : ''}
            onClick={() => selectPlayMode('standard')}
          >
            {t('home.standard')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={playMode === 'variants'}
            className={playMode === 'variants' ? 'active' : ''}
            onClick={() => selectPlayMode('variants')}
          >
            {t('home.variants')}
          </button>
        </div>

        {playMode === 'standard' ? (
          <>
            <div className="time-class-toggle" role="group" aria-label={t('home.timeClass')}>
              {TIME_CLASSES.map((value) => (
                <button
                  type="button"
                  key={value}
                  className={timeClass === value ? 'active' : ''}
                  aria-pressed={timeClass === value}
                  onClick={() => selectTimeClass(value)}
                >
                  {t(`class.${value}`)}
                </button>
              ))}
            </div>

            <div className="friendly-time-grid" role="group" aria-label={t('home.timeControl')}>
              {visibleControls.map((key) => (
                <button
                  type="button"
                  className={`time-card ${timeControl === key ? 'selected' : ''}`}
                  key={key}
                  aria-pressed={timeControl === key}
                  onClick={() => selectTimeControl(key)}
                >
                  <span className="time-value">{TIME_CONTROLS[key].display || key}</span>
                  <strong>{t(`class.${TIME_CONTROLS[key].class}`)}</strong>
                  <small>{t(`classCopy.${TIME_CONTROLS[key].class}`)}</small>
                </button>
              ))}
            </div>

            <label className="rated-option" htmlFor="rated-standard">
              <input
                id="rated-standard"
                name="rated-standard"
                type="checkbox"
                checked={rated && allowsRatedPlay(timeControl)}
                disabled={!allowsRatedPlay(timeControl)}
                onChange={(event) => setRated(event.target.checked)}
              />
              <span>
                <strong>{t('home.rated')}</strong>
                <small>
                  {allowsRatedPlay(timeControl)
                    ? t('home.ratedDetail')
                    : t('home.ratedUntimed')}
                </small>
              </span>
            </label>
          </>
        ) : (
          <>
            <p className="muted variant-note">{t('home.variantNote')}</p>
            <div className="variant-grid" role="group" aria-label={t('home.variantGroup')}>
              {PLAYABLE_VARIANTS.filter((entry) => entry.id !== 'standard').map((entry) => (
                <button
                  type="button"
                  key={entry.id}
                  className={`variant-card ${variant === entry.id ? 'selected' : ''}`}
                  aria-pressed={variant === entry.id}
                  onClick={() => setVariant(entry.id)}
                >
                  <strong>{entry.label}</strong>
                  <small>{entry.description}</small>
                </button>
              ))}
              {BACKLOG_VARIANTS.map((entry) => (
                <div key={entry.id} className="variant-card backlog" aria-disabled="true">
                  <strong>{entry.label}</strong>
                  <small>{entry.description}</small>
                  <span className="variant-badge">{t('home.comingSoon')}</span>
                </div>
              ))}
            </div>

            <div className="time-class-toggle" role="group" aria-label={t('home.variantTimeClass')}>
              {TIME_CLASSES.map((value) => (
                <button
                  type="button"
                  key={value}
                  className={timeClass === value ? 'active' : ''}
                  aria-pressed={timeClass === value}
                  onClick={() => selectTimeClass(value)}
                >
                  {t(`class.${value}`)}
                </button>
              ))}
            </div>

            <div className="friendly-time-grid" role="group" aria-label={t('home.timeControl')}>
              {visibleControls.map((key) => (
                <button
                  type="button"
                  className={`time-card ${timeControl === key ? 'selected' : ''}`}
                  key={key}
                  aria-pressed={timeControl === key}
                  onClick={() => selectTimeControl(key)}
                >
                  <span className="time-value">{TIME_CONTROLS[key].display || key}</span>
                  <strong>{t(`class.${TIME_CONTROLS[key].class}`)}</strong>
                  <small>{t(`classCopy.${TIME_CONTROLS[key].class}`)}</small>
                </button>
              ))}
            </div>

            <label className="rated-option" htmlFor="rated-variant">
              <input
                id="rated-variant"
                name="rated-variant"
                type="checkbox"
                checked={rated && allowsRatedPlay(timeControl)}
                disabled={!allowsRatedPlay(timeControl)}
                onChange={(event) => setRated(event.target.checked)}
              />
              <span>
                <strong>{t('home.rated')}</strong>
                <small>
                  {allowsRatedPlay(timeControl)
                    ? t('home.ratedVariant')
                    : t('home.ratedUntimed')}
                </small>
              </span>
            </label>
          </>
        )}

        <div className="challenge-timing">
          <fieldset className="start-mode-fieldset">
            <legend>{t('home.startLegend')}</legend>
            <label className="start-mode-option" htmlFor="start-mode-immediate">
              <input
                id="start-mode-immediate"
                type="radio"
                name="start-mode"
                checked={startMode === CHALLENGE_START_MODES.IMMEDIATE}
                onChange={() => setStartMode(CHALLENGE_START_MODES.IMMEDIATE)}
              />
              <span>
                <strong>{t('home.bothConfirm')}</strong>
                <small>{t('home.bothConfirmDetail')}</small>
              </span>
            </label>
            <label className="start-mode-option" htmlFor="start-mode-delay">
              <input
                id="start-mode-delay"
                type="radio"
                name="start-mode"
                checked={startMode === CHALLENGE_START_MODES.DELAY}
                onChange={() => setStartMode(CHALLENGE_START_MODES.DELAY)}
              />
              <span>
                <strong>{t('home.startIn')}</strong>
                <select
                  id="start-delay"
                  name="start-delay"
                  aria-label={t('home.startDelay')}
                  value={startDelayMs}
                  disabled={startMode !== CHALLENGE_START_MODES.DELAY}
                  onChange={(event) => setStartDelayMs(Number(event.target.value))}
                >
                  {CHALLENGE_DELAY_OPTIONS.map((option) => (
                    <option key={option.id} value={option.ms}>{duration(option.label)}</option>
                  ))}
                </select>
                <small>{t('home.forfeit', { minutes: Math.round(MATCH_FORFEIT_GRACE_MS / 60000) })}</small>
              </span>
            </label>
            <label className="start-mode-option" htmlFor="start-mode-scheduled">
              <input
                id="start-mode-scheduled"
                type="radio"
                name="start-mode"
                checked={startMode === CHALLENGE_START_MODES.SCHEDULED}
                onChange={() => setStartMode(CHALLENGE_START_MODES.SCHEDULED)}
              />
              <span>
                <strong>{t('home.scheduled')}</strong>
                <input
                  id="scheduled-start"
                  name="scheduled-start"
                  type="datetime-local"
                  min={scheduleMin}
                  value={scheduledStartDraft}
                  disabled={startMode !== CHALLENGE_START_MODES.SCHEDULED}
                  onChange={(event) => setScheduledStartDraft(event.target.value)}
                />
                <small>{t('home.scheduledDetail')}</small>
              </span>
            </label>
          </fieldset>

          <label htmlFor="join-wait-limit">
            <strong>{t('home.wait')}</strong>
            <select
              id="join-wait-limit"
              name="join-wait-limit"
              value={joinWaitMs}
              onChange={(event) => setJoinWaitMs(Number(event.target.value))}
            >
              {CHALLENGE_JOIN_WAIT_OPTIONS.map((option) => (
                <option key={option.id} value={option.ms}>{duration(option.label)}</option>
              ))}
            </select>
            <small>{t('home.waitDetail')}</small>
          </label>
        </div>

          <label className="settings-toggle challenge-preference" htmlFor="accept-challenges">
          <input
            id="accept-challenges"
            name="accept-challenges"
            type="checkbox"
            checked={openToChallenges}
            onChange={(event) => onToggleOpenToChallenges(event.target.checked)}
          />
          <span>
            <strong>{t('home.acceptLadder')}</strong>
            <small>
              {openToChallenges ? t('home.acceptLadderOn') : t('home.acceptLadderOff')}
            </small>
          </span>
        </label>

        <button
          type="button"
          className="create-game-button"
          onClick={createChallenge}
        >
          {t('home.invite')}
        </button>
      </section>

      <section className="how-it-works">
        <div>
          <span className="step-number">1</span>
          <h3>{t('home.step1')}</h3>
          <p>{t('home.step1Detail')}</p>
        </div>
        <div>
          <span className="step-number">2</span>
          <h3>{t('home.step2')}</h3>
          <p>{t('home.step2Detail')}</p>
        </div>
        <div>
          <span className="step-number">3</span>
          <h3>{t('home.step3')}</h3>
          <p>{t('home.step3Detail')}</p>
        </div>
      </section>

      <section className="values-strip">
        <span>{t('home.noRegistration')}</span>
        <span>{t('home.noAds')}</span>
        <span>{t('home.p2p')}</span>
        <span>✓ Verifiable games</span>
      </section>
    </>
  );
}
